import type { ComputedMetrics } from "@/types/metrics";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MetricDeltaRow } from "@/components/dashboard/MetricDeltaRow";
import { FormulaCardRow, type FormulaItem } from "@/components/dashboard/FormulaCardRow";
import { METRIC_TOOLTIPS } from "@/lib/metricTooltips";

function fmt1(n: number) {
  return n.toFixed(1);
}

interface MetricCardProps {
  label: string;
  definition: string;
  hours: number;
  pyHours: number;
  nvaHours: number;
  labelColor: string;
  // Block-offs and Available time are metrics where a rise is the bad
  // outcome — flips the delta pills' green/red meaning.
  invert?: boolean;
}

function MetricCard({
  label,
  definition,
  hours,
  pyHours,
  nvaHours,
  labelColor,
  invert,
}: MetricCardProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Card className="flex-1 rounded-lg px-3.5 py-4 gap-0 shadow-none bg-white border border-black/10">
          <div className="text-[12px] mb-1 font-medium" style={{ color: labelColor }}>
            {label}
          </div>
          <div className="text-[28px] font-semibold tracking-tight leading-none flex items-baseline gap-1.5 text-gray-900">
            {fmt1(hours)}
            <span className="text-[14px] font-normal text-gray-500">hours</span>
          </div>
          <div className="h-px my-2 bg-gray-200" />
          <div className="flex flex-col gap-1 text-gray-600">
            <MetricDeltaRow
              label="PY"
              compactLabel
              current={hours}
              baseline={pyHours}
              unit="h"
              invert={invert}
            />
            <MetricDeltaRow
              label="NVA"
              compactLabel
              current={hours}
              baseline={nvaHours}
              unit="h"
              invert={invert}
            />
          </div>
        </Card>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        showArrow={false}
        className="bg-white text-gray-600 border border-black/10 shadow-lg max-w-[260px]"
      >
        {definition}
      </TooltipContent>
    </Tooltip>
  );
}

interface UtilizationKPICardsProps {
  current: ComputedMetrics;
  priorYear: ComputedMetrics;
  nvaAvg: ComputedMetrics;
  typeFilterActive?: boolean;
}

export function UtilizationKPICards({
  current,
  priorYear,
  nvaAvg,
  typeFilterActive,
}: UtilizationKPICardsProps) {
  const seenStyle = { label: "var(--perf-green-dark)" };
  const otherStyle = { label: "var(--brand-dark-teal)" };
  const availStyle = { label: "#555" };
  const blockStyle = { label: "var(--perf-red-dark)" };

  const d = Math.max(current.dvmDays, 1);
  const pyd = Math.max(priorYear.dvmDays, 1);
  const nvad = Math.max(nvaAvg.dvmDays, 1);

  const items: FormulaItem[] = [
    {
      key: "worked",
      card: (
        <MetricCard
          label="Worked Hours/DVM/day"
          definition={METRIC_TOOLTIPS.worked}
          hours={current.avgWorkedHoursPerDay}
          pyHours={priorYear.avgWorkedHoursPerDay}
          nvaHours={nvaAvg.avgWorkedHoursPerDay}
          labelColor="#333"
        />
      ),
    },
    {
      key: "seen",
      op: "=",
      card: (
        <MetricCard
          label="Pets seen/DVM/day"
          definition={METRIC_TOOLTIPS.seen}
          hours={current.totalSeenMin / 60 / d}
          pyHours={priorYear.totalSeenMin / 60 / pyd}
          nvaHours={nvaAvg.totalSeenMin / 60 / nvad}
          labelColor={seenStyle.label}
        />
      ),
    },
    ...(typeFilterActive
      ? [
          {
            key: "other",
            op: "+",
            card: (
              <MetricCard
                label="Other appt types"
                definition={METRIC_TOOLTIPS.otherTypes}
                hours={current.totalOtherTypesMin / 60 / d}
                pyHours={priorYear.totalOtherTypesMin / 60 / pyd}
                nvaHours={nvaAvg.totalOtherTypesMin / 60 / nvad}
                labelColor={otherStyle.label}
              />
            ),
          },
        ]
      : []),
    {
      key: "blockoffs",
      op: "+",
      card: (
        <MetricCard
          label="Block-offs/DVM/day"
          definition={METRIC_TOOLTIPS.blockoff}
          hours={current.totalBlockoffMin / 60 / d}
          pyHours={priorYear.totalBlockoffMin / 60 / pyd}
          nvaHours={nvaAvg.totalBlockoffMin / 60 / nvad}
          labelColor={blockStyle.label}
          invert
        />
      ),
    },
    {
      key: "available",
      op: "+",
      card: (
        <MetricCard
          label="Available time/DVM/day"
          definition={METRIC_TOOLTIPS.available}
          hours={current.totalAvailMin / 60 / d}
          pyHours={priorYear.totalAvailMin / 60 / pyd}
          nvaHours={nvaAvg.totalAvailMin / 60 / nvad}
          labelColor={availStyle.label}
          invert
        />
      ),
    },
  ];

  return (
    <TooltipProvider>
      <div className="mb-7">
        <FormulaCardRow items={items} />
      </div>
    </TooltipProvider>
  );
}
